import React, { useState } from "react";
import { Spin, Select } from "antd";
import openNewIcon from "../../assets/UiElements/open_in_new.png";
import payIcon from "../../assets/UiElements/pay.png";
import withdrawIcon from "../../assets/UiElements/withdraw.png";
import addBeneIcon from "../../assets/UiElements/addbene.png";
import claimIcon from "../../assets/UiElements/claim.png";
import { useLocation } from "react-router-dom";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { InfoCircleFilled, FileTextOutlined } from "@ant-design/icons";
import { useDeleteBeneficiary } from "../../hooks/beneficiaries/useDeleteBeneficiary";
import { AddBeneficiaryModal } from "../../components/AddBeneficiaryModal";
import ClaimsModal from "../../components/ClaimsModal";
import WithdrawalRequestModal from "../../components/WithdrawalRequestModal";
import { useGetPremiumStatement } from "../../hooks/useGetPremiumStatement";
import { useGetInvestmentStatement } from "../../hooks/useGetInvestmentStatement";
import { useGetPolicyDocument } from "../../hooks/useGetPolicyDocument";
import { usePolicyReport } from "../../hooks/reports/useReport";
import { usePolicyReports } from "../../hooks/usePolicyReports";
import { getStatusMeta } from "../../util/policyStatus";
import { getUserToken, notifyUser } from "../../util/util";
import {
  useInitiateLifePayment,
  useLifePaymentHistory,
} from "../../hooks/usePayment";
import { PaymentOptionsModal } from "../../components/PaymentOptionsModal";
import { PaymentHistoryModal } from "../../components/PaymentHistoryModal";
import RecommendUpgrade from "../../components/RecommendUpgrade";
import {
  normalizeBeneficiaries,
  extractBeneficiaryListFromResponse,
  formatBeneficiaryPercentage,
  getInitials,
} from "../../util/beneficiaryUtils";
// Tooltip component for info icon
function InfoTooltip({ info }) {
  const [show, setShow] = React.useState(false);
  const hideTimeout = React.useRef();

  const handleMouseEnter = () => {
    clearTimeout(hideTimeout.current);
    setShow(true);
  };
  const handleMouseLeave = () => {
    hideTimeout.current = setTimeout(() => setShow(false), 180);
  };

  React.useEffect(() => () => clearTimeout(hideTimeout.current), []);

  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <InfoCircleFilled
        style={{
          color: "#111",
          fontSize: 17,
          marginLeft: 4,
          cursor: "pointer",
          verticalAlign: "middle",
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={() => setShow((v) => !v)}
      />
      {show && (
        <span
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#222",
            color: "#fff",
            padding: "7px 14px",
            borderRadius: 7,
            fontSize: 13,
            whiteSpace: "nowrap",
            zIndex: 10,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {info}
        </span>
      )}
      <style>{`
        .beneficiary-row { width: 100%; }
        .beneficiary-row div { box-sizing: border-box; }
        @media (max-width: 640px) {
          .beneficiary-row { flex-direction: column !important; align-items: flex-start !important; gap: 8px; }
          .beneficiary-row > div:first-child { width: 40px !important; height: 40px !important; font-size: 16px !important; margin-right: 12px !important; }
          .beneficiary-row .btn { padding: 8px 10px !important; font-size: 13px !important; }
        }
      `}</style>
    </span>
  );
}

function formatCurrency(value) {
  if (value === null || value === undefined || value === "") return "GHS 0.00";
  // Check if value is negative
  const isNegative = Number(value) < 0;
  // Remove any existing currency sign, commas, and minus signs, then format
  const num = Math.abs(Number(String(value).replace(/[^\d.]/g, "")));
  if (isNaN(num)) return value;
  // Format: GHS -34,000.00 (minus after GHS)
  const sign = isNegative ? "-" : "";
  return `GHS ${sign}${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function getDownloadUrl(url) {
  if (!url) return url;

  // For policy report download URLs, use the URL as-is since it already contains the correct token
  if (url.includes("/api/reports/") && url.includes("token=")) {
    // Ensure download=true is present
    if (!url.includes("download=")) {
      const separator = url.includes("?") ? "&" : "?";
      url += `${separator}download=true`;
    }
    return url;
  }

  const token = getUserToken();
  if (!token) return url;

  // Replace any existing token parameter with the session token
  let result = url.replace(/token=[^&]*/, `token=${token}`);

  // If no token parameter existed, add it
  if (!result.includes("token=")) {
    const separator = result.includes("?") ? "&" : "?";
    result += `${separator}token=${token}`;
  }

  // Ensure download=true is present
  if (!result.includes("download=")) {
    const separator = result.includes("?") ? "&" : "?";
    result += `${separator}download=true`;
  }

  return result;
}

async function downloadPdfWithAuth(url, filename) {
  try {
    const token = getUserToken();
    if (!token) {
      notifyUser(
        "error",
        "Please log in to download documents",
        "Authentication Required",
      );
      return;
    }

    // Ensure we use HTTPS from the start to avoid redirect issues
    let secureUrl = url;
    if (url.startsWith("http://")) {
      secureUrl = url.replace("http://", "https://");
    }

    const response = await fetch(secureUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename || "policy-document.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Download error:", error);
    notifyUser("error", "Failed to download document", "Download Error");
  }
}

function LifeProductDetails({ productDetails, beneficiaries }) {
  const [showModal, setShowModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [activeBeneficiary, setActiveBeneficiary] = useState(null);
  const [premiumStatementModalOpen, setPremiumStatementModalOpen] =
    useState(false);
  const [investmentStatementModalOpen, setInvestmentStatementModalOpen] =
    useState(false);
  const [policyDocumentModalOpen, setPolicyDocumentModalOpen] = useState(false);
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [withdrawalModalOpen, setWithdrawalModalOpen] = useState(false);
  const [paymentOptionsModalOpen, setPaymentOptionsModalOpen] = useState(false);
  const [paymentHistoryModalOpen, setPaymentHistoryModalOpen] = useState(false);
  const [selectedPremiumYears, setSelectedPremiumYears] = useState([]);
  const buttonContainerRef = React.useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  React.useEffect(() => {
    const container = buttonContainerRef.current;
    if (!container) return;

    const updateScrollState = () => {
      const el = buttonContainerRef.current;
      if (!el) return;
      setCanScrollLeft(el.scrollLeft > 8);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
    };

    updateScrollState();
    container.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);
    return () => {
      container.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, []);

  const { mutate: deleteBeneficiary, isLoading: deletingBeneficiary } =
    useDeleteBeneficiary();

  // Normalize incoming productDetails shape to a predictable object `pd` used by this UI
  const pd = React.useMemo(() => {
    if (!productDetails)
      return {
        policyName: "",
        policyNumber: "",
        status: "",
        totalPremiumPaid: 0,
        modalPremium: 0,
        sumAssured: 0,
        availableBalance: 0,
        totalRedemption: 0,
        premiumAmount: 0,
        premiumFrequency: "",
        startDate: "",
        endDate: "",
        premDueDate: "",
        lastPremDate: "",
        documents: [],
      };

    // Handle nested data structure from API - extract first policy from policy_details array
    const rawData =
      productDetails.data &&
      productDetails.data.policy_details &&
      productDetails.data.policy_details[0]
        ? productDetails.data.policy_details[0]
        : productDetails;

    // Map fields - rawData may have camelCase (from LifePage) or snake_case (from API)
    // Since rawData === productDetails when passed from LifePage, use camelCase directly
    const statusRaw = (rawData.status || rawData.Status || "").toString();
    const mappedData = {
      policyName: rawData.policyName || "",
      policyNumber: rawData.policyNumber || "",
      status: statusRaw,
      totalPremiumPaid:
        typeof rawData.totalPremiumPaid === "number"
          ? rawData.totalPremiumPaid
          : 0,
      modalPremium:
        typeof rawData.modalPremium === "number" ? rawData.modalPremium : 0,
      sumAssured:
        typeof rawData.sumAssured === "number" ? rawData.sumAssured : 0,
      availableBalance:
        typeof rawData.availableBalance === "number"
          ? rawData.availableBalance
          : 0,
      totalBenefits:
        (typeof rawData.sumAssured === "number" ? rawData.sumAssured : 0) +
        (typeof rawData.availableBalance === "number"
          ? rawData.availableBalance
          : 0),
      totalRedemption:
        typeof rawData.totalRedemption === "number"
          ? rawData.totalRedemption
          : 0,
      premiumAmount:
        typeof rawData.premiumAmount === "number" ? rawData.premiumAmount : 0,
      premiumFrequency: rawData.premiumFrequency || "",
      startDate: rawData.startDate || "",
      endDate: rawData.endDate || "",
      premDueDate: rawData.premDueDate || "",
      lastPremDate: rawData.lastPremDate || "",
      documents: Array.isArray(rawData.documents) ? rawData.documents : [],
    };

    return mappedData;
  }, [productDetails]);

  const normalizedBeneficiaries = React.useMemo(() => {
    if (!beneficiaries) return [];
    if (Array.isArray(beneficiaries))
      return normalizeBeneficiaries(beneficiaries);
    return normalizeBeneficiaries(
      extractBeneficiaryListFromResponse(beneficiaries),
    );
  }, [beneficiaries]);

  const statusMeta = React.useMemo(() => getStatusMeta(pd.status), [pd.status]);

  const [currentBeneficiaries, setCurrentBeneficiaries] = useState(
    normalizedBeneficiaries,
  );

  React.useEffect(() => {
    setCurrentBeneficiaries(normalizedBeneficiaries);
  }, [normalizedBeneficiaries]);

  const currentTotalPerc = currentBeneficiaries.reduce(
    (sum, b) => sum + (b.percAllocValue || 0),
    0,
  );

  // Edit/Delete actions have been hidden; helper functions removed to avoid unused warnings

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setActiveBeneficiary(null);
  };

  const confirmDelete = () => {
    if (!activeBeneficiary) return;
    deleteBeneficiary(
      { beneficiaryId: activeBeneficiary.beneficiaryId },
      {
        onSuccess: () => {
          setCurrentBeneficiaries((prev) =>
            prev.filter(
              (beneficiary) =>
                beneficiary.beneficiaryId !== activeBeneficiary.beneficiaryId,
            ),
          );
          closeDeleteModal();
        },
      },
    );
  };

  // Documents list for modal (from normalized pd)
  const documents = pd.documents || [];

  // Fetch policy report when document modal opens
  const {
    data: reportResult,
    isLoading: reportLoading,
    error: reportError,
    refetch: refetchReport,
  } = usePolicyReport(pd.policyNumber, "", { enabled: false });
  React.useEffect(() => {
    if (showModal && pd.policyNumber) {
      // trigger one-off fetch when modal opens
      refetchReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, pd.policyNumber]);

  // Generate year options (current year + 5 previous years)
  const yearOptions = React.useMemo(() => {
    const currentYear = new Date().getFullYear();
    const options = [];
    for (let i = 0; i < 5; i++) {
      const year = (currentYear - i).toString();
      options.push({ value: year, label: year });
    }
    return options;
  }, []);

  // Policy reports mutation (first API call)
  const {
    mutate: initiatePolicyReport,
    isLoading: initiatingPolicyReport,
  } = usePolicyReports();

  // Fetch premium statement when modal opens (enabled by modal state)
  const {
    data: premiumStatementData,
    isLoading: premiumStatementLoading,
    error: premiumStatementError,
    refetch: refetchPremiumStatement,
  } = useGetPremiumStatement(pd.policyNumber, premiumStatementModalOpen);

  // Reset premium statement filters when modal closes
  React.useEffect(() => {
    if (!premiumStatementModalOpen) {
      setSelectedPremiumYears([]);
    }
  }, [premiumStatementModalOpen]);

  // Generate report button handler
  const handleGenerateReport = () => {
    if (pd.policyNumber && selectedPremiumYears.length > 0) {
      // First API call: initiate report generation
      initiatePolicyReport(
        { policyNumber: pd.policyNumber, year: selectedPremiumYears },
        {
          onSuccess: () => {
            // Second API call: fetch generated reports (no year filter)
            refetchPremiumStatement();
          },
          onError: () => {
            // Still try to fetch existing reports even if initiation fails
            refetchPremiumStatement();
          },
        }
      );
    }
  };

  // Fetch investment statement when modal opens (enabled by modal state)
  const {
    data: investmentStatementData,
    isLoading: investmentStatementLoading,
    error: investmentStatementError,
    // eslint-disable-next-line no-unused-vars
    refetch: refetchInvestmentStatement,
  } = useGetInvestmentStatement(pd.policyNumber, investmentStatementModalOpen);

  // Fetch policy document when modal opens (enabled by modal state)
  const {
    data: policyDocumentData,
    isLoading: policyDocumentLoading,
    error: policyDocumentError,
    // eslint-disable-next-line no-unused-vars
    refetch: refetchPolicyDocument,
  } = useGetPolicyDocument(pd.policyNumber, policyDocumentModalOpen);

  // Payment hooks
  const initiateLifePayment = useInitiateLifePayment();
  // Payment history hook - filter by policy number
  const {
    data: lifePaymentHistory,
    isLoading: paymentHistoryLoading,
    refetch: refetchPaymentHistory,
  } = useLifePaymentHistory({ policy_number: pd.policyNumber });

  // Payment handler functions
  const handlePayNow = async (paymentData) => {
    try {
      const paymentRequestData = {
        amount: paymentData.amount,
        currency: paymentData.currency || "GHS",
        policyNumber: pd.policyNumber,
        product: pd.policyName || "Life Insurance",
      };

      const result = await initiateLifePayment.mutateAsync(paymentRequestData);

      if (result?.success && result?.data?.checkoutUrl) {
        // Redirect to payment checkout
        window.open(result.data.checkoutUrl, "_blank");
        notifyUser("success", "Payment initiated successfully", "Payment");
      } else {
        notifyUser("error", "Failed to initiate payment", "Payment Error");
      }
    } catch (error) {
      console.error("Payment initiation error:", error);
      notifyUser(
        "error",
        error.message || "Failed to initiate payment",
        "Payment Error",
      );
    }
  };

  const handleViewPaymentHistory = () => {
    setPaymentHistoryModalOpen(true);
    // Refresh payment history when opening modal
    refetchPaymentHistory();
  };

  return (
    <div
      className="life-details-page"
      style={{
        minHeight: "80vh",
        background: "#f5f6fa",
        padding: 24,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 32,
      }}
    >
      <style>
        {`
          .button-card {
            transition: transform 0.3s ease;
          }
          .button-card:hover {
            transform: scale(1.05);
          }
          .button-container::-webkit-scrollbar {
            display: none;
          }
          .button-container {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .tab-scroll-btn:disabled {
            pointer-events: none;
            opacity: 0;
          }
          .tab-scroll-btn:hover:not(:disabled) {
            transform: translateY(-50%) scale(1.08);
            box-shadow: 0 12px 28px rgba(0, 176, 80, 0.45);
          }
          @media (max-width: 768px) {
            .product-details-wrapper {
              max-width: 100% !important;
              padding: 0 8px !important;
            }
            .product-details-card {
              max-width: 100% !important;
              padding: 20px 12px !important;
              border-radius: 12px !important;
              overflow: visible !important;
            }
            .product-details-card > div[style*="overflow: hidden"] {
              overflow: visible !important;
            }
            .button-card {
              min-width: 56px !important;
              max-width: 56px !important;
              padding: 3px 2px !important;
              gap: 3px !important;
            }
            .button-card img {
              width: 14px !important;
              height: 14px !important;
            }
            .button-card .anticon {
              font-size: 14px !important;
            }
            .button-card span {
              font-size: 7px !important;
              line-height: 1.1 !important;
            }
            .tab-scroll-btn {
              display: none !important;
            }
            .life-details-page {
              padding: 12px 8px !important;
              gap: 12px !important;
            }
            .product-details-wrapper {
              gap: 12px !important;
            }
            .policy-modal-content {
              padding: 0 16px 16px 16px !important;
            }
            .policy-modal-header {
              padding: 16px 16px 0 16px !important;
            }
            .policy-report-row {
              flex-direction: column !important;
              align-items: flex-start !important;
              gap: 10px !important;
            }
            .policy-doc-meta {
              flex-direction: column !important;
              align-items: flex-start !important;
              gap: 8px !important;
            }
            .policy-doc-actions {
              margin-left: 0 !important;
              width: 100% !important;
            }
            .policy-doc-actions .btn {
              width: 100% !important;
            }
          }
        `}
      </style>
      <RecommendUpgrade
        currentPremium={pd.premiumAmount || 0}
        suggestedPremium={(pd.premiumAmount || 0) * 1.1}
        additionalBenefit={(pd.premiumAmount || 0) * 0.1}
        onUpgrade={() => {
          console.log("Upgrade initiated for life policy:", pd.policyNumber);
        }}
        onNotInterested={() => {
          console.log(
            "User not interested in upgrade for life policy:",
            pd.policyNumber,
          );
        }}
      />
      <div
        className="product-details-wrapper"
        style={{
          width: "100%",
          maxWidth: 700,
          display: "flex",
          flexDirection: "column",
          gap: 24,
          alignItems: "center",
        }}
      >
        {/* Product Details Card */}
        <div
          className="product-details-card"
          style={{
            borderRadius: 18,
            boxShadow: "0 4px 24px 0 rgba(0,0,0,0.10)",
            padding: "32px 48px",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 0,
            maxWidth: 700,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontWeight: 800,
              fontSize: 20,
              color: "#000000ff",
              marginBottom: 12,
              width: "100%",
            }}
          >
            <span>{productDetails.policyName}</span>
          </div>
          <div
            style={{ position: "relative", width: "100%", overflow: "hidden" }}
          >
            <div
              className="buttons-scroll-wrapper"
              style={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                alignItems: "center",
                width: "100%",
              }}
            >
              <button
                type="button"
                className="tab-scroll-btn"
                aria-label="Scroll buttons left"
                onClick={() => {
                  const container = buttonContainerRef.current;
                  if (container)
                    container.scrollBy({ left: -120, behavior: "smooth" });
                }}
                disabled={!canScrollLeft}
                style={{
                  left: 0,
                  opacity: canScrollLeft ? 1 : 0,
                  position: "relative",
                  flexShrink: 0,
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#00b050",
                  boxShadow: "0 10px 25px rgba(0, 176, 80, 0.35)",
                  cursor: "pointer",
                  transition: "opacity 0.2s, transform 0.2s, box-shadow 0.2s",
                  zIndex: 30,
                  color: "#fff",
                  marginRight: 8,
                }}
              >
                <ChevronLeft size={18} />
              </button>
              <div
                className="button-container"
                ref={buttonContainerRef}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "nowrap",
                  gap: 10,
                  overflowX: "auto",
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  flex: "1 1 auto",
                  minWidth: 0,
                }}
              >
                {/* 1. Pay Now */}
                <div
                  className="button-card"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 4,
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: 8,
                    cursor: "pointer",
                    minWidth: 100,
                    flexShrink: 0,
                  }}
                  onClick={() => setPaymentOptionsModalOpen(true)}
                >
                  <img
                    src={payIcon}
                    alt="Pay Now"
                    style={{ width: 24, height: 24 }}
                  />
                  <span
                    style={{
                      fontSize: 12,
                      textAlign: "center",
                      fontWeight: 600,
                      color: "#00704A",
                    }}
                  >
                    Pay Now
                  </span>
                </div>

                {/* 2. Policy Document */}
                <div
                  className="button-card"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 4,
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: 8,
                    cursor: "pointer",
                    minWidth: 100,
                    flexShrink: 0,
                  }}
                  onClick={() => setPolicyDocumentModalOpen(true)}
                >
                  <FileTextOutlined
                    style={{ fontSize: 24, color: "#00704A" }}
                  />
                  <span
                    style={{
                      fontSize: 12,
                      textAlign: "center",
                      fontWeight: 600,
                      color: "#00704A",
                    }}
                  >
                    Policy Document
                  </span>
                </div>

                {/* 3. Premium Statement */}
                <div
                  className="button-card"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 4,
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: 8,
                    cursor: "pointer",
                    minWidth: 100,
                    flexShrink: 0,
                  }}
                  onClick={() => setPremiumStatementModalOpen(true)}
                >
                  <FileTextOutlined
                    style={{ fontSize: 24, color: "#00704A" }}
                  />
                  <span
                    style={{
                      fontSize: 12,
                      textAlign: "center",
                      fontWeight: 600,
                      color: "#00704A",
                    }}
                  >
                    Premium Statement
                  </span>
                </div>

                {/* 4. Investment Statement - only show if cash value > 0 */}
                {parseFloat(pd.availableBalance) > 0 && (
                  <div
                    className="button-card"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 4,
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: 8,
                      cursor: "pointer",
                      minWidth: 100,
                      flexShrink: 0,
                    }}
                    onClick={() => setInvestmentStatementModalOpen(true)}
                  >
                    <FileTextOutlined
                      style={{ fontSize: 24, color: "#00704A" }}
                    />
                    <span
                      style={{
                        fontSize: 12,
                        textAlign: "center",
                        fontWeight: 600,
                        color: "#00704A",
                      }}
                    >
                      Investment Statement
                    </span>
                  </div>
                )}

                {/* 5. Claim */}
                <div
                  className="button-card"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 4,
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: 8,
                    cursor: "pointer",
                    minWidth: 100,
                    flexShrink: 0,
                  }}
                  onClick={() => {
                    if (process.env.REACT_APP_COMING_SOON_BUTTONS === "true") {
                      notifyUser("info", "Coming Soon!", "Info");
                    } else {
                      setClaimModalOpen(true);
                    }
                  }}
                >
                  <img
                    src={claimIcon}
                    alt="Claim"
                    style={{ width: 24, height: 24 }}
                  />
                  <span
                    style={{
                      fontSize: 12,
                      textAlign: "center",
                      fontWeight: 600,
                      color: "#00704A",
                    }}
                  >
                    Claim
                  </span>
                </div>
                <div
                  className="button-card"
                  style={{
                    display: "none",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 4,
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: 8,
                    cursor: "pointer",
                    minWidth: 100,
                    flexShrink: 0,
                  }}
                  onClick={() => {
                    if (process.env.REACT_APP_COMING_SOON_BUTTONS === "true") {
                      notifyUser("info", "Coming Soon!", "Info");
                    } else {
                      setWithdrawalModalOpen(true);
                    }
                  }}
                >
                  <img
                    src={withdrawIcon}
                    alt="Withdraw"
                    style={{ width: 24, height: 24 }}
                  />
                  <span
                    style={{
                      fontSize: 12,
                      textAlign: "center",
                      fontWeight: 600,
                      color: "#00704A",
                    }}
                  >
                    Withdraw
                  </span>
                </div>
                {/* Add Beneficiary card hidden as per request */}
                <div className="button-card" style={{ display: "none" }}>
                  <img
                    src={addBeneIcon}
                    alt="Add Beneficiary"
                    style={{ width: 24, height: 24 }}
                  />
                  <span
                    style={{
                      fontSize: 12,
                      textAlign: "center",
                      fontWeight: 600,
                      color: "#00704A",
                    }}
                  >
                    Add Beneficiary
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="tab-scroll-btn"
                aria-label="Scroll buttons right"
                onClick={() => {
                  const container = buttonContainerRef.current;
                  if (container)
                    container.scrollBy({ left: 120, behavior: "smooth" });
                }}
                disabled={!canScrollRight}
                style={{
                  opacity: canScrollRight ? 1 : 0,
                  position: "relative",
                  flexShrink: 0,
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#00b050",
                  boxShadow: "0 10px 25px rgba(0, 176, 80, 0.35)",
                  cursor: "pointer",
                  transition: "opacity 0.2s, transform 0.2s, box-shadow 0.2s",
                  zIndex: 30,
                  color: "#fff",
                  marginLeft: 8,
                }}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
          <div style={{ height: 18 }} />
          <div style={{ display: "flex", flexDirection: "column" }}>
            {/* ...existing code... */}
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {productDetails.policyNumber}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Policy Number
              </div>
            </div>
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
                position: "relative",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{ color: "#000000ff", fontWeight: 700, fontSize: 18 }}
                >
                  {statusMeta.label}
                </span>
                <InfoTooltip info={statusMeta.description} />
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Policy Status
              </div>
            </div>
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {formatCurrency(pd.totalPremiumPaid)}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Total Premium Paid
              </div>
            </div>
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {formatCurrency(pd.modalPremium)}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Modal Premium
              </div>
            </div>
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {formatCurrency(pd.sumAssured)}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Sum Assured
              </div>
            </div>
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {formatCurrency(pd.availableBalance)}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Cash Value
              </div>
            </div>
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {formatCurrency(pd.totalBenefits)}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Total Benefits
              </div>
            </div>

            {/* Premium Amount Row */}
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {formatCurrency(pd.premiumAmount)}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Monthly Premium
              </div>
            </div>
            {/* Premium Frequency Row */}
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {pd.premiumFrequency}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Premium Frequency
              </div>
            </div>
            {/* Premium Due Date Row */}
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {formatDate(pd.premDueDate)}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Premium Due Date
              </div>
            </div>
            {/* Last Premium Date Row */}
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {formatDate(pd.lastPremDate)}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Last Premium Date
              </div>
            </div>
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {formatDate(pd.startDate)}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Premium Issued Date
              </div>
            </div>
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {formatDate(pd.endDate)}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Premium Maturity Date
              </div>
            </div>
          </div>
        </div>

        {/* Modal for Policy Documents */}
        {showModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.18)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => setShowModal(false)}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 18,
                minWidth: 320,
                maxWidth: 700,
                width: "100%",
                boxShadow: "0 8px 40px 0 rgba(0,0,0,0.13)",
                padding: 0,
                position: "relative",
                display: "flex",
                flexDirection: "column",
                maxHeight: "80vh",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div
                className="policy-modal-header"
                style={{ padding: "24px 32px 0 32px" }}
              >
                <div
                  style={{
                    fontWeight: 800,
                    fontSize: 20,
                    color: "#222",
                    marginBottom: 6,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span>{pd.policyName}</span>
                  <button
                    onClick={() => setShowModal(false)}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: 22,
                      color: "#888",
                      cursor: "pointer",
                      marginLeft: 12,
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                <p
                  style={{
                    color: "#888",
                    fontSize: 15,
                    marginBottom: 18,
                    marginTop: 0,
                  }}
                >
                  See all policy documents available
                </p>
              </div>

              {/* Scrollable body */}
              <div
                className="policy-modal-content"
                style={{
                  padding: "0 32px 24px 32px",
                  overflowY: "auto",
                  paddingBottom: 32,
                }}
              >
                {/* Policy Report(s) */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontWeight: 700 }}>Policy Reports</div>
                  </div>

                  {reportLoading ? (
                    <div style={{ padding: 24, textAlign: "center" }}>
                      <Spin size="small" style={{ marginRight: 8 }} />
                      <span style={{ color: "#666", fontSize: 14 }}>
                        Generating report...
                      </span>
                    </div>
                  ) : reportError ? (
                    <div style={{ color: "red" }}>
                      {(() => {
                        // Handle specific API errors
                        if (
                          reportError?.message ===
                          "You have exceeded your Requests."
                        ) {
                          return "Request limit exceeded. Please try again later.";
                        }
                        if (reportError?.message === "Not Found.") {
                          return "The requested report could not be found.";
                        }
                        // Show actual API error message if available, fallback to generic message
                        return String(
                          reportError?.message ||
                            "Failed to load policy report",
                        );
                      })()}
                    </div>
                  ) : (
                    (() => {
                      console.log("Report result from hook:", reportResult); // Debug log

                      // Get reports array from response
                      const reports = reportResult?.data || [];

                      if (reportResult?.success && reports.length > 0) {
                        console.log("Using reports:", reports);

                        return (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 8,
                            }}
                          >
                            {reports.map((report) => {
                              const reportId = report?.report_id || report?.id;
                              const status = report?.status;

                              // Handle completed/downloaded status
                              if (status === "completed" || status === "downloaded") {
                                return (
                                  <div
                                    key={reportId}
                                    className="policy-report-row"
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      background: "#f2f4f6",
                                      padding: 16,
                                      borderRadius: 8,
                                    }}
                                  >
                                    <div style={{ flex: 1 }}>
                                      <div
                                        style={{ fontWeight: 700, marginBottom: 4 }}
                                      >
                                        Policy Report #{reportId}
                                      </div>
                                      <div
                                        style={{
                                          color: "#666",
                                          fontSize: 13,
                                          marginBottom: 2,
                                        }}
                                      >
                                        <span>
                                          <strong>Status:</strong>{" "}
                                          <span style={{ color: "#52c41a" }}>
                                            ✓ {status}
                                          </span>
                                        </span>
                                      </div>
                                      <div
                                        style={{
                                          color: "#666",
                                          fontSize: 13,
                                          marginBottom: 2,
                                        }}
                                      >
                                        <span>
                                          <strong>Type:</strong>{" "}
                                          {report.type || "Policy Contributions"}
                                        </span>
                                      </div>
                                      <div
                                        style={{
                                          color: "#666",
                                          fontSize: 13,
                                          marginBottom: 2,
                                        }}
                                      >
                                        <span>
                                          <strong>Generated:</strong>{" "}
                                          {formatDate(
                                            report.created_at || new Date(),
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                    <div style={{ display: "flex", gap: 8 }}>
                                      {report.download_url && (
                                        <button
                                          className="btn btn-primary"
                                          onClick={() =>
                                            downloadPdfWithAuth(
                                              report.download_url,
                                              `policy-report-${reportId}.pdf`,
                                            )
                                          }
                                          style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 6,
                                              fontWeight: 600,
                                            }}
                                          >
                                            <i
                                              className="bi bi-download"
                                              style={{ fontSize: 16 }}
                                            ></i>
                                            Download
                                          </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              }

                              // Handle pending/processing status
                              if (status === "pending" || status === "processing") {
                                return (
                                  <div
                                    key={reportId}
                                    style={{
                                      background: "#fff7e6",
                                      border: "1px solid #ffd591",
                                      padding: 16,
                                      borderRadius: 8,
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 12,
                                      }}
                                    >
                                      <Spin size="small" />
                                      <div style={{ flex: 1 }}>
                                        <div
                                          style={{
                                            fontWeight: 600,
                                            marginBottom: 4,
                                            color: "#d46b08",
                                          }}
                                        >
                                          Report is being generated
                                        </div>
                                        <div
                                          style={{ color: "#666", fontSize: 13 }}
                                        >
                                          Report #{reportId} is currently {status}.
                                          This may take a few minutes.
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              }

                              // Unknown status
                              return (
                                <div
                                  key={reportId}
                                  style={{
                                    background: "#f5f5f5",
                                    border: "1px solid #d9d9d9",
                                    padding: 16,
                                    borderRadius: 8,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <span>Report #{reportId} - {status}</span>
                                    <button
                                      className="btn btn-outline-primary"
                                      onClick={() => refetchReport()}
                                      style={{
                                        fontWeight: 600,
                                        fontSize: 13,
                                        padding: "6px 12px",
                                      }}
                                    >
                                      <i
                                        className="bi bi-arrow-clockwise"
                                        style={{ marginRight: 4 }}
                                      ></i>
                                      Refresh
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => refetchReport()}
                              style={{ 
                                fontWeight: 600, 
                                fontSize: 13,
                                alignSelf: "flex-start"
                              }}
                            >
                              <i
                                className="bi bi-arrow-clockwise"
                                style={{ marginRight: 4 }}
                              ></i>
                              Refresh Reports
                            </button>
                          </div>
                        );
                      }

                      // No report or empty state
                      return (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: 12,
                            background: "#f8f9fa",
                            borderRadius: 8,
                          }}
                        >
                          <span style={{ color: "#666" }}>
                            No policy report available yet.
                          </span>
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => refetchReport()}
                            style={{ fontWeight: 600, fontSize: 13 }}
                          >
                            <i
                              className="bi bi-plus"
                              style={{ marginRight: 4 }}
                            ></i>
                            Generate Report
                          </button>
                        </div>
                      );
                    })()
                  )}
                </div>

                {/* Documents list */}
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 18 }}
                >
                  {documents.map((doc, idx) => (
                    <div
                      key={idx}
                      style={{
                        background: "#f8f9fa",
                        borderRadius: 10,
                        padding: "18px 18px 14px 18px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          color: "#222",
                          fontSize: 16,
                          marginBottom: 2,
                        }}
                      >
                        {doc.name}
                      </div>
                      <div
                        className="policy-doc-meta"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 18,
                          justifyContent: "space-between",
                        }}
                      >
                        <div
                          style={{
                            color: "#888",
                            fontSize: 13,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          Uploaded {formatDate(doc.uploaded)}
                          <span
                            style={{
                              display: "inline-block",
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              backgroundColor: "#ccc",
                              margin: "0 8px",
                              verticalAlign: "middle",
                            }}
                          ></span>
                          {doc.size}
                        </div>
                        <div
                          className="policy-doc-actions"
                          style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                            flexShrink: 0,
                            marginLeft: "auto",
                          }}
                        >
                          <button
                            className="btn btn-white"
                            style={{
                              padding: "4px 14px",
                              fontWeight: 600,
                              fontSize: 14,
                            }}
                            onClick={() =>
                              window.open(getDownloadUrl(doc.url), "_blank")
                            }
                          >
                            Download
                          </button>
                          <button
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: 0,
                              display: "flex",
                              alignItems: "center",
                            }}
                            onClick={() =>
                              window.open(getDownloadUrl(doc.url), "_blank")
                            }
                          >
                            <img
                              src={openNewIcon}
                              alt="Download"
                              style={{ width: 26, height: 26 }}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        {premiumStatementModalOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.18)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => setPremiumStatementModalOpen(false)}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 18,
                minWidth: 320,
                maxWidth: 700,
                width: "100%",
                boxShadow: "0 8px 40px 0 rgba(0,0,0,0.13)",
                padding: 0,
                position: "relative",
                display: "flex",
                flexDirection: "column",
                maxHeight: "80vh",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{ padding: "24px 32px 0 32px" }}>
                <div
                  style={{
                    fontWeight: 800,
                    fontSize: 20,
                    color: "#222",
                    marginBottom: 6,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span>Premium Statement</span>
                  <button
                    onClick={() => setPremiumStatementModalOpen(false)}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: 22,
                      color: "#888",
                      cursor: "pointer",
                      marginLeft: 12,
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                <p
                  style={{
                    color: "#888",
                    fontSize: 15,
                    marginBottom: 18,
                    marginTop: 0,
                  }}
                >
                  Download your premium statement
                </p>
              </div>

              {/* Scrollable body */}
              <div
                style={{
                  padding: "0 32px 24px 32px",
                  overflowY: "auto",
                  paddingBottom: 32,
                }}
              >
                {/* Year Selector */}
                <div style={{ marginBottom: 20 }}>
                  <label
                    style={{
                      display: "block",
                      fontWeight: 600,
                      fontSize: 14,
                      color: "#444",
                      marginBottom: 8,
                    }}
                  >
                    Select Year(s)
                  </label>
                  <Select
                    mode="multiple"
                    placeholder="Select years"
                    value={selectedPremiumYears}
                    onChange={(value) => setSelectedPremiumYears(value)}
                    disabled={initiatingPolicyReport || premiumStatementLoading}
                    options={yearOptions}
                    style={{ width: "100%", marginBottom: 12 }}
                    dropdownStyle={{ zIndex: 10000 }}
                    getPopupContainer={(trigger) => trigger.parentNode}
                  />
                  
                  {/* Generate Button - Always visible below dropdown */}
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleGenerateReport}
                    disabled={
                      selectedPremiumYears.length === 0 ||
                      initiatingPolicyReport ||
                      premiumStatementLoading
                    }
                  >
                    {initiatingPolicyReport ? (
                      <>
                        <Spin size="small" />
                        Generating...
                      </>
                    ) : (
                      <>Generate Report</>
                    )}
                  </button>
                </div>

                {premiumStatementLoading || initiatingPolicyReport ? (
                  <div style={{ padding: 24, textAlign: "center" }}>
                    <Spin />
                    <p style={{ color: "#666", fontSize: 14, marginTop: 12 }}>
                      {initiatingPolicyReport ? "Generating report..." : "Loading..."}
                    </p>
                  </div>
                ) : premiumStatementError ? (
                  <div style={{ color: "red" }}>
                    {(() => {
                      const message =
                        premiumStatementError?.response?.data?.message ||
                        premiumStatementError?.message ||
                        "Failed to load premium statement";
                      return message;
                    })()}
                  </div>
                ) : premiumStatementData?.success &&
                  premiumStatementData?.data?.length > 0 ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                    }}
                  >
                    {premiumStatementData.data
                      .filter(report => (report.status === "completed" || report.status === "downloaded") && report.download_url)
                      .map((report) => (
                        <div
                          key={report.id}
                          style={{
                            background: "#f8f9fa",
                            borderRadius: 10,
                            padding: "18px 18px 14px 18px",
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                            position: "relative",
                          }}
                        >
                          <div
                            style={{
                              color: "#666",
                              fontSize: 13,
                              marginBottom: 4,
                            }}
                          >
                            Generated: {formatDate(report.created_at)}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 18,
                              justifyContent: "space-between",
                            }}
                          >
                            <div
                              style={{
                                color: "#888",
                                fontSize: 13,
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              Premium Statement PDF
                              <span
                                style={{
                                  display: "inline-block",
                                  width: 6,
                                  height: 6,
                                  borderRadius: "50%",
                                  backgroundColor: "#ccc",
                                  margin: "0 8px",
                                  verticalAlign: "middle",
                                }}
                              ></span>
                              application/pdf
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                alignItems: "center",
                                flexShrink: 0,
                                marginLeft: "auto",
                              }}
                            >
                              <button
                                className="btn btn-primary"
                                style={{
                                  padding: "4px 14px",
                                  fontWeight: 600,
                                  fontSize: 14,
                                }}
                                onClick={() =>
                                  downloadPdfWithAuth(
                                    report.download_url,
                                    `premium-statement-${report.id}.pdf`,
                                  )
                                }
                              >
                                Download
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    {premiumStatementData.data.filter(r => (r.status === "completed" || r.status === "downloaded") && r.download_url).length === 0 && (
                      <div style={{ color: "#666", textAlign: "center", padding: 24 }}>
                        No completed premium statements available.
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ color: "#666" }}>
                    No premium statement available.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Investment Statement Modal */}
        {investmentStatementModalOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.18)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => setInvestmentStatementModalOpen(false)}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 18,
                minWidth: 320,
                maxWidth: 700,
                width: "100%",
                boxShadow: "0 8px 40px 0 rgba(0,0,0,0.13)",
                padding: 0,
                position: "relative",
                display: "flex",
                flexDirection: "column",
                maxHeight: "80vh",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div
                className="policy-modal-header"
                style={{ padding: "24px 32px 0 32px" }}
              >
                <div
                  style={{
                    fontWeight: 800,
                    fontSize: 20,
                    color: "#222",
                    marginBottom: 6,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span>Investment Statement</span>
                  <button
                    onClick={() => setInvestmentStatementModalOpen(false)}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: 22,
                      color: "#888",
                      cursor: "pointer",
                      marginLeft: 12,
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                <p
                  style={{
                    color: "#888",
                    fontSize: 15,
                    marginBottom: 18,
                    marginTop: 0,
                  }}
                >
                  Download your investment statement
                </p>
              </div>

              {/* Scrollable body */}
              <div
                className="policy-modal-content"
                style={{
                  padding: "0 32px 24px 32px",
                  overflowY: "auto",
                  paddingBottom: 32,
                }}
              >
                {investmentStatementLoading ? (
                  <div style={{ padding: 24, textAlign: "center" }}>
                    <Spin />
                  </div>
                ) : investmentStatementError ? (
                  <div style={{ color: "red" }}>
                    {(() => {
                      const message =
                        investmentStatementError?.response?.data?.message ||
                        investmentStatementError?.message ||
                        "Failed to load investment statement";
                      return message;
                    })()}
                  </div>
                ) : investmentStatementData?.success &&
                  investmentStatementData?.data?.data ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 18,
                    }}
                  >
                    <div
                      style={{
                        background: "#f8f9fa",
                        borderRadius: 10,
                        padding: "18px 18px 14px 18px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          color: "#222",
                          fontSize: 16,
                          marginBottom: 2,
                          wordBreak: "break-all",
                          overflowWrap: "break-word",
                        }}
                      >
                        {investmentStatementData.data.fileName}
                      </div>
                      <div
                        className="policy-doc-meta"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 18,
                          justifyContent: "space-between",
                        }}
                      >
                        <div
                          style={{
                            color: "#888",
                            fontSize: 13,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          Investment Statement PDF
                          <span
                            style={{
                              display: "inline-block",
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              backgroundColor: "#ccc",
                              margin: "0 8px",
                              verticalAlign: "middle",
                            }}
                          ></span>
                          {investmentStatementData.data.mimeType}
                        </div>
                        <div
                          className="policy-doc-actions"
                          style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                            flexShrink: 0,
                            marginLeft: "auto",
                          }}
                        >
                          <button
                            className="btn btn-primary"
                            style={{
                              padding: "4px 14px",
                              fontWeight: 600,
                              fontSize: 14,
                            }}
                            onClick={() => {
                              const link = document.createElement("a");
                              link.href = `data:${investmentStatementData.data.mimeType};base64,${investmentStatementData.data.data}`;
                              link.download =
                                investmentStatementData.data.fileName;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                          >
                            Download
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ color: "#666" }}>
                    No investment statement available.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Policy Document Modal */}
        {policyDocumentModalOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.18)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => setPolicyDocumentModalOpen(false)}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 18,
                minWidth: 320,
                maxWidth: 700,
                width: "100%",
                boxShadow: "0 8px 40px 0 rgba(0,0,0,0.13)",
                padding: 0,
                position: "relative",
                display: "flex",
                flexDirection: "column",
                maxHeight: "80vh",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div
                className="policy-modal-header"
                style={{ padding: "24px 32px 0 32px" }}
              >
                <div
                  style={{
                    fontWeight: 800,
                    fontSize: 20,
                    color: "#222",
                    marginBottom: 6,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span>Policy Document</span>
                  <button
                    onClick={() => setPolicyDocumentModalOpen(false)}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: 22,
                      color: "#888",
                      cursor: "pointer",
                      marginLeft: 12,
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                <p
                  style={{
                    color: "#888",
                    fontSize: 15,
                    marginBottom: 18,
                    marginTop: 0,
                  }}
                >
                  Download your policy document
                </p>
              </div>

              {/* Scrollable body */}
              <div
                className="policy-modal-content"
                style={{
                  padding: "0 32px 24px 32px",
                  overflowY: "auto",
                  paddingBottom: 32,
                }}
              >
                {policyDocumentLoading ? (
                  <div style={{ padding: 24, textAlign: "center" }}>
                    <Spin />
                  </div>
                ) : policyDocumentError ? (
                  <div style={{ color: "red" }}>
                    {(() => {
                      const message =
                        policyDocumentError?.response?.data?.message ||
                        policyDocumentError?.message ||
                        "Failed to load policy document";
                      return message;
                    })()}
                  </div>
                ) : policyDocumentData?.success &&
                  policyDocumentData?.data?.url ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 18,
                    }}
                  >
                    <div
                      style={{
                        background: "#f8f9fa",
                        borderRadius: 10,
                        padding: "18px 18px 14px 18px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          color: "#222",
                          fontSize: 16,
                          marginBottom: 2,
                          wordBreak: "break-all",
                          overflowWrap: "break-word",
                        }}
                      >
                        {policyDocumentData.data.url.split("/").pop()}
                      </div>
                      <div
                        className="policy-doc-meta"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 18,
                          justifyContent: "space-between",
                        }}
                      >
                        <div
                          style={{
                            color: "#888",
                            fontSize: 13,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          Policy Document PDF
                          <span
                            style={{
                              display: "inline-block",
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              backgroundColor: "#ccc",
                              margin: "0 8px",
                              verticalAlign: "middle",
                            }}
                          ></span>
                          application/pdf
                        </div>
                        <div
                          className="policy-doc-actions"
                          style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                            flexShrink: 0,
                            marginLeft: "auto",
                          }}
                        >
                          <button
                            className="btn btn-primary"
                            style={{
                              padding: "4px 14px",
                              fontWeight: 600,
                              fontSize: 14,
                            }}
                            onClick={() =>
                              window.open(
                                getDownloadUrl(policyDocumentData.data.url),
                                "_blank",
                              )
                            }
                          >
                            Download
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ color: "#666" }}>
                    No policy document available.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Beneficiary Card */}
        <div
          className="product-details-card"
          style={{
            borderRadius: 18,
            boxShadow: "0 4px 24px 0 rgba(0,0,0,0.10)",
            padding: "32px 48px",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 0,
            maxWidth: 700,
          }}
        >
          <div
            style={{
              fontWeight: 800,
              fontSize: 20,
              color: "#000000ff",
              marginBottom: 12,
            }}
          >
            {currentBeneficiaries && currentBeneficiaries.length === 1
              ? "Beneficiary"
              : "Beneficiaries"}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {!currentBeneficiaries || currentBeneficiaries.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "32px 0",
                  color: "#888",
                }}
              >
                {/* Multi-user/group icon SVG */}
                <svg
                  width="60"
                  height="60"
                  viewBox="0 0 60 60"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginBottom: 16 }}
                >
                  <circle cx="30" cy="30" r="30" fill="#f5f6fa" />
                  {/* Main user */}
                  <ellipse cx="30" cy="27" rx="10" ry="10" fill="#d9d9d9" />
                  {/* Left user */}
                  <ellipse cx="18" cy="32" rx="6" ry="6" fill="#e0e0e0" />
                  {/* Right user */}
                  <ellipse cx="42" cy="32" rx="6" ry="6" fill="#e0e0e0" />
                  {/* Group base */}
                  <ellipse cx="30" cy="46" rx="17" ry="8" fill="#d9d9d9" />
                  <ellipse cx="18" cy="46" rx="8" ry="4" fill="#e0e0e0" />
                  <ellipse cx="42" cy="46" rx="8" ry="4" fill="#e0e0e0" />
                </svg>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 17,
                    color: "#888",
                    textAlign: "center",
                  }}
                >
                  No beneficiaries assigned to this policy
                </div>
              </div>
            ) : (
              currentBeneficiaries.map((b, idx) => {
                const initials = getInitials(b.name);
                const split = formatBeneficiaryPercentage(b);
                return (
                  <div
                    className="beneficiary-row"
                    key={b.beneficiaryId || b.name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      borderTop: idx !== 0 ? "1px solid #eee" : undefined,
                      padding: "18px 0",
                      textAlign: "left",
                      position: "relative",
                      transition: "background 0.2s, border-radius 0.2s",
                      cursor: "pointer",
                      borderRadius: 0,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#e6f9ed";
                      e.currentTarget.style.borderRadius = "12px";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "";
                      e.currentTarget.style.borderRadius = "0";
                    }}
                  >
                    {/* Profile left */}
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        background: "#111",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        color: "#fff",
                        fontSize: 20,
                        marginRight: 18,
                        flexShrink: 0,
                      }}
                    >
                      {initials}
                    </div>
                    {/* Details center */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 500,
                          color: "#222",
                          fontSize: 18,
                          lineHeight: 1.2,
                        }}
                      >
                        {b.name}
                      </div>
                      <div
                        style={{
                          color: "#888",
                          fontWeight: 500,
                          fontSize: 15,
                          marginTop: 2,
                        }}
                      >
                        {split} Split
                      </div>
                      <div
                        style={{
                          color: "#888",
                          fontWeight: 500,
                          fontSize: 15,
                          marginTop: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 18,
                        }}
                      >
                        <span>{b.relationship}</span>
                        {b.dob ? (
                          <>
                            <span
                              style={{
                                fontSize: 17,
                                fontWeight: 400,
                                color: "#bbb",
                              }}
                            >
                              -
                            </span>
                            <span>{formatDate(b.dob)}</span>
                          </>
                        ) : null}
                      </div>
                    </div>
                    {/* Edit/Delete actions hidden per request */}
                    <div style={{ display: "flex", gap: 10 }} />
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
      {deleteModalOpen && activeBeneficiary && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.18)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={closeDeleteModal}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              width: "100%",
              maxWidth: 460,
              padding: "28px 32px",
              boxShadow: "0 16px 60px rgba(0,0,0,0.15)",
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 10 }}>
              Remove Beneficiary
            </div>
            <p style={{ color: "#666", fontSize: 15, lineHeight: 1.4 }}>
              Are you sure you want to remove{" "}
              <strong>{activeBeneficiary.name}</strong> from this policy? This
              action cannot be undone.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 12,
                marginTop: 18,
              }}
            >
              <button
                type="button"
                className="btn btn-white"
                onClick={closeDeleteModal}
                style={{ padding: "10px 18px", fontWeight: 600 }}
              >
                Keep Beneficiary
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{
                  padding: "10px 18px",
                  fontWeight: 600,
                  background: "#C62828",
                  color: "#fff",
                  borderColor: "#C62828",
                }}
                onClick={confirmDelete}
                disabled={deletingBeneficiary}
              >
                {deletingBeneficiary ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
      <AddBeneficiaryModal
        isOpen={addModalOpen}
        onClose={() => {
          setAddModalOpen(false);
          setIsEdit(false);
          setActiveBeneficiary(null);
        }}
        currentTotalPerc={currentTotalPerc}
        isEdit={isEdit}
        beneficiaryToEdit={activeBeneficiary}
        onEditSuccess={(updatedBeneficiary) => {
          setCurrentBeneficiaries((prev) =>
            prev.map((b) =>
              b.beneficiaryId === updatedBeneficiary.beneficiaryId
                ? updatedBeneficiary
                : b,
            ),
          );
        }}
      />
      <ClaimsModal
        isOpen={claimModalOpen}
        onClose={() => setClaimModalOpen(false)}
        policyNumber={pd.policyNumber}
        productType="LIFE"
        policyName={pd.policyName}
        availableCashValue={pd.availableBalance}
      />
      <WithdrawalRequestModal
        isOpen={withdrawalModalOpen}
        onClose={() => setWithdrawalModalOpen(false)}
        policyNumber={pd.policyNumber}
        productType="LIFE"
      />

      {/* Payment Options Modal */}
      <PaymentOptionsModal
        isOpen={paymentOptionsModalOpen}
        onClose={() => setPaymentOptionsModalOpen(false)}
        onPayNow={handlePayNow}
        onViewHistory={handleViewPaymentHistory}
        productType="Life"
        totalAmount={pd.premiumAmount || pd.modalPremium || 0}
        currency="GHS"
        status={pd.status}
      />

      {/* Debug info */}
      {console.log("LifeProductDetails - Payment modal props:", {
        premiumAmount: pd.premiumAmount,
        modalPremium: pd.modalPremium,
        totalAmount: pd.premiumAmount || pd.modalPremium || 0,
        policyNumber: pd.policyNumber,
      }) || null}

      {/* Payment History Modal */}
      <PaymentHistoryModal
        isOpen={paymentHistoryModalOpen}
        onClose={() => setPaymentHistoryModalOpen(false)}
        payments={lifePaymentHistory?.data || []}
        isLoading={paymentHistoryLoading}
        productType="Life"
      />
    </div>
  );
}

// Default export: wrapper that reads from location state for dynamic navigation
export default function LifeProductDetailsWrapper() {
  const location = useLocation();

  const { productDetails, beneficiaries } = location.state || {};

  if (!productDetails) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 32,
        }}
      >
        <div style={{ textAlign: "center", maxWidth: 720 }}>
          <h3 style={{ marginBottom: 8 }}>No product selected</h3>
          <p style={{ color: "#666", marginBottom: 16 }}>
            Please open a product from the dashboard or products list to see the
            investment details.
          </p>
          <div>
            <a
              href="/dashboard"
              className="btn btn-white"
              style={{ marginRight: 8 }}
            >
              Back to Dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <LifeProductDetails
      productDetails={productDetails}
      beneficiaries={beneficiaries || []}
    />
  );
}

// Also export the component for direct use
export { LifeProductDetails };
